import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { serializeQueryParams } from '~publish/hooks/useQueryParam'

export interface TagReportingLinkProps {
  /**
   * The `href` for the "Open Reporting" anchor. Kept on the anchor so
   * right-click / open-in-new-tab and no-JS still work.
   */
  href: string
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void
}

/**
 * Resolves where a tag's "Open Reporting" action should point: the in-app
 * filtered Insights view (`/insights?tags[]=<id>`).
 *
 * Returns a builder so it can be used for a single tag or mapped over many
 * (e.g. the tag management dialog) without breaking the rules of hooks.
 *
 * @see CORE-6295
 */
export function useTagReportingLink(): (
  tagId?: string,
) => TagReportingLinkProps {
  const navigate = useNavigate()

  return useCallback(
    (tagId?: string): TagReportingLinkProps => {
      const href = tagId
        ? `/insights?${serializeQueryParams({ tags: [tagId] })}`
        : '/insights'

      return {
        href,
        onClick: (event: React.MouseEvent<HTMLAnchorElement>): void => {
          event.preventDefault()
          navigate(href)
        },
      }
    },
    [navigate],
  )
}
